import React, {useId} from "react";
import BrandColorPalette from "account/brand/BrandColorPalette";
import {POSTER_COLOR_PRESETS} from "account/brand/brandPalette";
import {OptionError, OptionField, OptionHint, OptionLegend} from "./optionControls.styles";

/**
 * One of the wallpaper poster's two colour rows: the TEXT COLOUR or the TEXT BACKGROUND
 * (kitecorp/transferify-ui#2416).
 *
 * Mounts {@link BrandColorPalette}, the same swatch row a sender uses for their accent on the account
 * Brand sheet and in the personalization builder, over the poster's five colours
 * ({@link POSTER_COLOR_PRESETS}) and with the builder's "no colour" swatch first. A second colour control
 * here would be a second keyboard model and a second set of colour names to translate, for a value with
 * the identical `#rrggbb` grammar.
 *
 * "No colour" is a real choice with a visible result, which is why it is a swatch rather than a button
 * beside the row: with no text colour the poster derives one that reads on what is behind the words, and
 * with no background it measures the picture and veils the words only where it must (`posterLayout.ts`,
 * `posterContrast.ts`). The swatch is checked exactly when the option is unset.
 *
 * It replaces `BrandColorField`, which offered all 36 accents, a hex readout and a quiet "Use the text
 * colour" button for the advertiser's separate accent. The owner asked for a small set with a no-colour
 * swatch on both rows, and the accent is now the text colour itself.
 *
 * Dumb by mandate (epic #26): value in, `undefined` or a colour out.
 */
export interface PosterColorFieldProps {
	label: string;
	hint: string;
	/** The accessible name of the no-colour swatch, which differs per row ("No colour", "No background"). */
	noColorLabel: string;
	/** The chosen colour, or `undefined` when none is set. */
	value: string | undefined;
	onChange: (color: string | undefined) => void;
	error: string | null;
}

export function PosterColorField(
	{label, hint, noColorLabel, value, onChange, error}: PosterColorFieldProps,
): React.ReactElement {
	// `useId` rather than a hand-picked string: two of these sit in one panel, and three surfaces mount it.
	const id = useId();
	const legendId = `${id}-legend`;
	return (
		<OptionField>
			{/* The palette is a radiogroup labelled BY this legend, so the id has to match. */}
			<OptionLegend id={legendId}>{label}</OptionLegend>
			<OptionHint>{hint}</OptionHint>
			<BrandColorPalette
				// An empty string is the palette's own "no colour here", which it reads as the clear stop.
				value={value ?? ""}
				colors={POSTER_COLOR_PRESETS}
				disabled={false}
				labelledBy={legendId}
				clear={{label: noColorLabel, onClear: () => onChange(undefined)}}
				onPick={onChange}
			/>
			{error !== null && <OptionError role="alert">{error}</OptionError>}
		</OptionField>
	);
}
